'use client';
import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';

import { useForm } from 'react-hook-form';
import { useState } from 'react';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { signIn } from '@eliadmin/core/src/api/customers/customers-api';
import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';

import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { zodResolver } from '@hookform/resolvers/zod';
import { usePathname, useRouter } from 'next/navigation';

import { FiEye, FiEyeOff } from 'react-icons/fi';
import { z } from 'zod';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Paragraph } from '../ui/paragraph';
import { MdAlternateEmail } from 'react-icons/md';
import { BsKey } from 'react-icons/bs';
import { HiOutlineArrowRightCircle } from 'react-icons/hi2';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { SignInDialog } from "@eliadmin/core/src/components/dialog/lazy-dialogs";

export const signInSchema = (VM: FormValidationMessages) =>
  z.object({
    email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
    password: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
    'g-recaptcha-response': z.string().default(''),
  });

type SignInFormProps = {
  onSuccess?: () => void;
  initialSocialAuthProviders?: SocialAuthProviderItem[];
  inDialog?: boolean;
  onShowRegister?: () => void;
  onShowForgot?: () => void;
};

export function SignInForm({
  onSuccess,
  initialSocialAuthProviders,
  onShowRegister,
  onShowForgot,
  inDialog = false,
}: SignInFormProps) {
  const pathname = usePathname();
  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);

  const form = useForm<z.infer<ReturnType<typeof signInSchema>>>({
    resolver: zodResolver(signInSchema(formValidationMessages)),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const [showPassword, setShowPassword] = useState(false);
  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const { close } = useDialogStore();

  const handleSubmit = form.handleSubmit(async data => {
    try {

      const resp = await signIn({ ...data,  locale });

      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });

      toast({ title: t('text656'), variant: 'success' });
      if (onSuccess) {
        onSuccess();
        return;
      }

      if (pathname === ROUTES.CART()) {
        router.push(`${ROUTES.CART()}?cs=billing`);
        router.refresh();
      } else {
        router.replace(ROUTES.PROFILE());
      }

      if (inDialog) {
        close(SignInDialog.id);
      }
    } catch (e) {
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text594'),
        description: (e as any)?.message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
        <CaptchaFormMessage errors={form.formState.errors} />
      <form
        onSubmit={handleSubmit}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-8',
          'ea-sign-in-form',
          !inDialog && 'py-12 sm:py-16 lg:py-24',
        )}
      >
        <div className="flex w-full flex-col items-center justify-center gap-3">
          <h4 className="text-center text-[28px] font-medium tracking-tighter sm:text-3xl lg:text-4xl">
            {t('text190')}
          </h4>
          <Paragraph className="text-center">
            {t('text2292')}&nbsp;
            <span
              className="cursor-pointer font-medium lowercase text-accent transition-all hover:opacity-80"
              onClick={inDialog ? onShowRegister : () => router.push(ROUTES.REGISTER())}
            >
              {t('text2019')}
            </span>
          </Paragraph>
        </div>

        <SocialAuthButtons
          className="ea-sign-in-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
        />

        <div className="ea-sign-in-form__content flex w-full flex-col items-center justify-center gap-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem required className="ea-sign-in-form__email-field w-full">
                <FormControl>
                  <Input
                    {...field}
                    placeholder="Email *"
                    before={<MdAlternateEmail size={24} className="text-[#9E9E9E]" />}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem required className="ea-sign-in-form__password-field w-full">
                <FormControl>
                  <div className="relative">
                    <Input
                      {...field}
                      placeholder={`${t('text191')} *`}
                      type={showPassword ? 'text' : 'password'}
                      before={<BsKey size={24} className="text-[#9E9E9E]" />}
                      after={
                        <Button
                          type="button"
                          variant="none"
                          size="icon"
                          onMouseDown={() => setShowPassword(true)}
                          onMouseUp={() => setShowPassword(false)}
                          onMouseLeave={() => setShowPassword(false)}
                          onTouchStart={() => setShowPassword(true)}
                          onTouchEnd={() => setShowPassword(false)}
                        >
                          {showPassword ? (
                            <FiEyeOff size={24} className="text-primary" />
                          ) : (
                            <FiEye size={24} className="text-primary" />
                          )}
                        </Button>
                      }
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          type="submit"
          className="ea-sign-in-form__submit-button"
          isLoading={form.formState.isSubmitting}
        >
          {t('text189')} <HiOutlineArrowRightCircle size={24} />
        </Button>

        <p
          onClick={inDialog ? onShowForgot : () => router.push(ROUTES.PASSWORD_RESET())}
          className="mx-auto w-fit cursor-pointer text-center text-sm font-medium text-primary transition-all hover:opacity-80 sm:text-base"
        >
          {t('text2345')}
        </p>
      </form>
    </Form>
  );
}
